import type { ReactElement } from "react"
import styled from "styled-components"
import { Provenance } from "~/features/provenance"
import { RemoteImage } from "~/shared/ui"
import type { StudyMaterial } from "../types"

/** A book cover, not a video still — the session rail's 16:9 card would letterbox it. */
const COVER_WIDTH = 220
const COVER_HEIGHT = 330

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`

const Heading = styled.h2`
  align-self: flex-start;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.color.textPrimary};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.color.textPrimary};
`

const Rail = styled.ul.attrs({ role: "list" })`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing.xxs} 0 ${theme.spacing.md}`};
  overflow-x: auto;
`

const Item = styled.li`
  flex: 0 0 auto;
  width: ${COVER_WIDTH}px;
  max-width: 100%;
`

const CoverLink = styled.a`
  display: block;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.md};

  &:hover h3 {
    color: ${({ theme }) => theme.color.linkHover};
    text-decoration: underline;
  }
`

const MaterialCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  min-width: 0;
`

const MaterialTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.color.textPrimary};
  overflow-wrap: anywhere;
`

interface MaterialRailProps {
  readonly title: string
  readonly material: StudyMaterial
  readonly qaHook: string
}

/**
 * The books that accompany a study.
 *
 * Rendered only when one resolved — there is no empty state, deliberately. The
 * pairing is a title heuristic (`studyEbookIndex.ts`), so a study whose book we
 * failed to find must be indistinguishable from a study that has no book. An
 * empty "Study Materials" section would announce a gap the app cannot actually
 * prove exists.
 */
export const MaterialRail = ({ title, material, qaHook }: MaterialRailProps): ReactElement => (
  <Root aria-label={title} data-qa-hook={`${qaHook}-section`}>
    <Heading>{title}</Heading>
    <Rail>
      <Item>
        <Provenance source={material.source}>
          <CoverLink
            href={material.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${material.title} (opens in Lifeway Reader)`}
            data-qa-hook={`${qaHook}-link`}
            data-reader-id-kind={material.linkIdKind}
          >
            <MaterialCard data-qa-hook={`${qaHook}-card`}>
              <RemoteImage
                src={material.coverUrl}
                alt=""
                ratio={COVER_WIDTH / COVER_HEIGHT}
                qaHook={`${qaHook}-cover`}
              />
              <MaterialTitle>{material.title}</MaterialTitle>
            </MaterialCard>
          </CoverLink>
        </Provenance>
      </Item>
    </Rail>
  </Root>
)
